import { useCallback, useEffect } from 'react';

import { blurChannel, enterChannel, focusChannel, leaveChannel, teardownChannel } from '../lib/channelReadLifecycle';
import { recordChannelView } from '../lib/recordChannelView';
import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import { useDocumentTitleContext } from '../hooks/useDocumentTitleEvents';
import UnreadReporter from './sidebar/UnreadReporter';

// Wires React/DOM lifecycle into the read-state policy (lib/channelReadLifecycle.js).
// No read-state logic lives here — only event detection and workspace navigation memory.
const UnreadManagerBase = ({ activeChannelId, activeWorkspaceId, unreadReportChannelIds }) => {
  const { workspaceId: routeWorkspaceId, profileId } = useRouteWorkspace();
  const workspaceId = activeWorkspaceId || routeWorkspaceId;
  const { setUnreadIndicator } = useDocumentTitleContext();
  const reportingEnabled = unreadReportChannelIds !== undefined;
  const serverReadStateEnabled = Boolean(profileId);

  // Window lifecycle: focus/blur snapshot the overlay; pagehide/unload get the
  // fire-and-forget teardown path because async Dexie reads may not finish.
  useEffect(() => {
    const handleEvent = (event) => {
      if (!activeChannelId || !workspaceId) return;

      switch (event.type) {
        case 'focus':
          if (serverReadStateEnabled) recordChannelView(activeChannelId);
          focusChannel({ channelId: activeChannelId });
          break;
        case 'blur':
          blurChannel({ channelId: activeChannelId });
          break;
        case 'pagehide':
        case 'unload':
          teardownChannel({ workspaceId, channelId: activeChannelId, profileId, via: event.type });
          break;
      }
    };

    window.addEventListener('focus', handleEvent);
    window.addEventListener('blur', handleEvent);
    window.addEventListener('pagehide', handleEvent);
    window.addEventListener('unload', handleEvent);
    return () => {
      window.removeEventListener('focus', handleEvent);
      window.removeEventListener('blur', handleEvent);
      window.removeEventListener('pagehide', handleEvent);
      window.removeEventListener('unload', handleEvent);
    };
  }, [activeChannelId, workspaceId, profileId, serverReadStateEnabled]);

  // Entering a channel marks it read on the spot; leaving does a final durable
  // advance. MessageFeed keeps the durable cursor at the exact rendered tail while
  // the channel stays open.
  useEffect(() => {
    if (!activeChannelId || !workspaceId) return undefined;

    if (serverReadStateEnabled) recordChannelView(activeChannelId);
    enterChannel({ workspaceId, channelId: activeChannelId, profileId });

    return () => {
      leaveChannel({ workspaceId, channelId: activeChannelId, profileId });
    };
  }, [activeChannelId, workspaceId, profileId, serverReadStateEnabled]);

  const handleOutputUnreadCounts = useCallback(
    async function handleOutputUnreadCountsCallback(event) {
      if (!event.detail || !Array.isArray(event.detail.unreadCounts)) return;

      const mentionsCount = event.detail.unreadCounts.reduce((sum, data) => sum + (data.mentions || 0), 0);
      const messagesCount = event.detail.unreadCounts.reduce((sum, data) => sum + (data.messages || 0), 0);
      const activityCount = event.detail.activityCount || 0;

      const indicator = mentionsCount > 0 ? mentionsCount : activityCount > 0 ? activityCount : messagesCount > 0 ? '•' : '';
      setUnreadIndicator(indicator);

      if (!window.electron?.events || !window.electron?.events['output:unread-counts']) return;
      await window.electron.events['output:unread-counts'](event.detail);
    },
    [setUnreadIndicator],
  );

  useEffect(() => {
    if (!reportingEnabled) return;

    window.addEventListener('output:unread-counts', handleOutputUnreadCounts);
    return () => {
      window.removeEventListener('output:unread-counts', handleOutputUnreadCounts);
    };
  }, [reportingEnabled, handleOutputUnreadCounts]);

  return reportingEnabled ? <UnreadReporter channelIds={unreadReportChannelIds} activeChannelId={activeChannelId} /> : null;
};

export const UnreadManager = UnreadManagerBase;
export default UnreadManager;
